import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Separator,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@roger/ui-components';
import { Loader2, Users } from 'lucide-react';
import type { OrgMemberResponse } from '../../clinician-manager/types';
import type { AgencyClinicResponse } from '../../clinician-manager/types';
import {
  RoleAssignmentContent,
  type RoleAssignmentHandle,
} from './RoleAssignmentDialog';
import {
  EditUserInfoSection,
  type EditUserInfoHandle,
} from './EditUserInfoSection';
import { RogerDocAccountSection } from '../../clinician-manager/RogerDocAccountSection';
import { UserLicensesSection } from '../../clinician-manager/UserLicensesSection';
import { useClinicDisciplines } from '../../clinician-manager/useClinicDisciplines';
import { EmployeeTypeSection } from '../../clinician-manager/EmployeeTypeSection';
import {
  UserTypeSection,
  type UserTypeSectionHandle,
} from '../../clinician-manager/UserTypeSection';
import { useEmployeeTypes } from '../../clinician-manager/useEmployeeTypes';
import {
  useRoleAssignmentDialog,
  RoleAssignmentSaveError,
} from './useRoleAssignmentDialog';
import { ClinicAccessSection } from '../../clinician-manager/ClinicAccessSection';
import { useShowEmrUserManagement } from '../../../hooks/useShowEmrUserManagement';
import { FeatureFlag } from '../../../constants/featureFlags';
import {
  updateClinicAccess,
  updateClinicDisciplines,
  updateEmployeeTypes,
} from '../../../services/api';
import { useStore } from '../../../store';
import { useShallow } from 'zustand/react/shallow';

type MemberSettingsDialogProps = {
  member: OrgMemberResponse | null;
  clinics: AgencyClinicResponse[];
  /** Kinnser-synced clinic IDs hidden from the picker; existing role assignments are preserved on save. */
  preserveRoleAssignmentsForClinicIds?: ReadonlySet<string>;
  onClose: () => void;
  onSuccess: () => void;
  /** Refresh RogerDoc link data without closing the dialog. */
  onRogerDocLinkChanged?: () => void;
};

export const MemberSettingsDialog: React.FC<MemberSettingsDialogProps> =
  React.memo(function MemberSettingsDialog({
    member,
    clinics,
    preserveRoleAssignmentsForClinicIds,
    onClose,
    onSuccess,
    onRogerDocLinkChanged,
  }) {
    const { hasPermission, hasFeatureFlag, showError } = useStore(
      useShallow(state => ({
        hasPermission: state.hasPermission,
        hasFeatureFlag: state.hasFeatureFlag,
        showError: state.showError,
      }))
    );
    const isSuperUser = hasPermission('admin:super-user');
    // Clinical titles, clinic access and employee type all describe somebody who
    // takes visits. An agency with no EMR schedules none, so the sections are
    // three questions about a product they do not have. The roles, the Roger App
    // account and the licences below are the parts that still mean something.
    //
    // Licences are the section that looks like it belongs with those three and
    // does not. Staff credential compliance is not an EMR concern, which is why
    // the License Types catalog in Admin is not `rogerEmrOnly`, so hiding the
    // per-person list here would leave an agency configuring licence types it
    // could no longer assign to anyone.
    const showEmr = useShowEmrUserManagement();
    // Clinic roles decide who can be scheduled for a visit, which an agency
    // with no EMR never does.
    const showClinicRoles =
      hasFeatureFlag(FeatureFlag.SHOW_CLINIC_INFO_IN_USER_MANAGER) && showEmr;
    // Licenses are per person and carry no clinic, so unlike the clinic-access
    // and employee-type sections below they are not narrowed by the operator's
    // own clinic set. They sit with the name and phone number, which the same
    // agency membership bounds.
    const canViewLicenses =
      hasPermission('user-licenses:view') ||
      hasPermission('user-licenses:edit');
    const canEditLicenses = hasPermission('user-licenses:edit');
    // A User Manager admin already administers these members, and the route
    // that writes the profile authorizes name and phone edits with the same
    // permission, so the dialog offers both. Roger-employee status is the one
    // field that stays super-user only.
    const canEditUserInfo =
      (isSuperUser || hasPermission('clinician-mgr:access')) &&
      !!member?.roger_user_id;

    const saveDisciplinesRef = useRef<() => Promise<void>>(async () => {});
    const saveEmployeeTypesRef = useRef<() => Promise<void>>(async () => {});
    const saveClinicAccessRef = useRef<() => Promise<void>>(async () => {});
    const saveUserTypeRef = useRef<UserTypeSectionHandle>(null);
    const validateEmployeeTypesRef = useRef<() => void>(() => {});

    // Validation has to run before any write: roles, clinic roles and
    // disciplines are all committed by the time onAfterRoleSave fires, so
    // rejecting there would leave the user with a save that half happened.
    const onBeforeRoleSave = useCallback(async () => {
      validateEmployeeTypesRef.current();
    }, []);

    const onAfterRoleSave = useCallback(async () => {
      await saveDisciplinesRef.current();
      await saveEmployeeTypesRef.current();
      await saveUserTypeRef.current?.saveIfDirty();
      // Last on purpose: the role save re-grants access only for posted
      // clinic-role pairs (create_user_clinic_entry), so the access
      // reconcile must run after it to be the authoritative final state.
      await saveClinicAccessRef.current();
    }, []);

    const visibleClinicIds = useMemo(
      () => new Set(clinics.map(clinic => clinic.id)),
      [clinics]
    );

    const roleAssignment = useRoleAssignmentDialog(
      member,
      onSuccess,
      onBeforeRoleSave,
      onAfterRoleSave,
      { preserveRoleAssignmentsForClinicIds, visibleClinicIds }
    );

    const {
      rows: disciplineRows,
      isDirty: disciplinesDirty,
      toggleDiscipline,
      selectAllDisciplinesForClinic,
      clearDisciplinesForClinic,
      selectAllDisciplines,
      clearAllDisciplines,
      getAssignments: getDisciplineAssignments,
    } = useClinicDisciplines(member, {
      pendingAssignableClinicIds: roleAssignment.assignableClinicIds,
      clinics,
    });

    const clinicDisciplines = useMemo(
      () =>
        member?.roger_user_id
          ? {
              rows: disciplineRows,
              onToggleDiscipline: toggleDiscipline,
              onSelectAllDisciplines: selectAllDisciplines,
              onClearAllDisciplines: clearAllDisciplines,
              onSelectAllForClinic: selectAllDisciplinesForClinic,
              onClearForClinic: clearDisciplinesForClinic,
            }
          : undefined,
      [
        member?.roger_user_id,
        disciplineRows,
        toggleDiscipline,
        selectAllDisciplines,
        clearAllDisciplines,
        selectAllDisciplinesForClinic,
        clearDisciplinesForClinic,
      ]
    );

    useEffect(() => {
      saveDisciplinesRef.current = async () => {
        if (!disciplinesDirty || !member?.roger_user_id) return;
        try {
          await updateClinicDisciplines({
            userId: member.roger_user_id,
            assignments: getDisciplineAssignments(),
            throwOnError: true,
          });
        } catch (error) {
          console.error('Failed to save clinic disciplines:', error);
          showError('Failed to save clinic disciplines. Please try again.');
          throw new RoleAssignmentSaveError('disciplines save failed');
        }
      };
    }, [
      disciplinesDirty,
      getDisciplineAssignments,
      member?.roger_user_id,
      showError,
    ]);

    // ── Clinic access (EMR-783) ────────────────────────────────────────────
    // The set the backend clinic filter enforces, edited directly. Clinics
    // holding a selected clinic role are force-checked in the section and
    // re-included on save, because the role save re-grants them anyway.
    const [accessClinicIds, setAccessClinicIds] = useState<Set<string>>(
      new Set()
    );
    const initialAccessClinicIdsRef = useRef<Set<string>>(new Set());
    useEffect(() => {
      const ids = new Set(
        (member?.clinic_access ?? []).map(entry => entry.clinic_id)
      );
      setAccessClinicIds(ids);
      initialAccessClinicIdsRef.current = new Set(ids);
    }, [member?.roger_user_id, member?.clinic_access]);

    const roleSelectedClinicIds = useMemo(() => {
      const ids = new Set<string>();
      for (const clinicIds of roleAssignment.clinicSelection.values()) {
        for (const clinicId of clinicIds) ids.add(clinicId);
      }
      return ids;
    }, [roleAssignment.clinicSelection]);

    // What this dialog will leave the member with once it saves. The employee
    // type rows follow it, so revoking a clinic stops offering one for it.
    const accessibleClinicIds = useMemo(
      () => new Set([...accessClinicIds, ...roleSelectedClinicIds]),
      [accessClinicIds, roleSelectedClinicIds]
    );

    const { removeClinicFromAllRoles, clearClinicRevocation } = roleAssignment;

    const toggleAccessClinic = useCallback(
      (clinicId: string) => {
        // A clinic holding a selected clinic role reads as checked even when it
        // is not in the access set yet, so the intent has to be read off the
        // same value the row renders.
        const isChecked =
          roleSelectedClinicIds.has(clinicId) || accessClinicIds.has(clinicId);
        if (isChecked) {
          // Revoking access gives up the clinic roles held there too. Keeping
          // them would re-grant the access on save and leave the member
          // assignable at a clinic nobody meant them to have.
          removeClinicFromAllRoles(clinicId);
        } else {
          // Changed their mind before saving. The roles they could see stay
          // cleared, but a hidden approver here should not be dropped over a
          // revoke that is no longer happening.
          clearClinicRevocation(clinicId);
        }
        setAccessClinicIds(prev => {
          // A clinic checked only by its role is not in this set, so there is
          // nothing to delete and no reason to hand back a new one.
          if (isChecked && !prev.has(clinicId)) return prev;
          const next = new Set(prev);
          if (isChecked) next.delete(clinicId);
          else next.add(clinicId);
          return next;
        });
      },
      [
        accessClinicIds,
        roleSelectedClinicIds,
        removeClinicFromAllRoles,
        clearClinicRevocation,
      ]
    );

    useEffect(() => {
      saveClinicAccessRef.current = async () => {
        if (!member?.roger_user_id) return;
        const visibleRoleClinicIds = [...roleSelectedClinicIds].filter(id =>
          visibleClinicIds.has(id)
        );
        const finalIds = new Set([...accessClinicIds, ...visibleRoleClinicIds]);
        const initial = initialAccessClinicIdsRef.current;
        const unchanged =
          finalIds.size === initial.size &&
          [...finalIds].every(id => initial.has(id));
        if (unchanged) return;
        try {
          await updateClinicAccess({
            path: { user_id: member.roger_user_id },
            body: { clinic_ids: [...finalIds] },
            throwOnError: true,
          });
        } catch (error) {
          console.error('Failed to save clinic access:', error);
          showError('Failed to save clinic access. Please try again.');
          throw new RoleAssignmentSaveError('clinic access save failed');
        }
      };
    }, [
      accessClinicIds,
      roleSelectedClinicIds,
      visibleClinicIds,
      member?.roger_user_id,
      showError,
    ]);

    const {
      rows: employeeTypeRows,
      isDirty: employeeTypesDirty,
      invalidClinicIds: invalidEmployeeTypeClinicIds,
      setEmployeeType,
      setEmployeeTypeOther,
      getAssignments: getEmployeeTypeAssignments,
    } = useEmployeeTypes(member, {
      clinics,
      visibleClinicIds: accessibleClinicIds,
    });

    useEffect(() => {
      validateEmployeeTypesRef.current = () => {
        if (!employeeTypesDirty || !member?.roger_user_id) return;
        if (invalidEmployeeTypeClinicIds.size > 0) {
          showError(
            'Enter an employee type for clinics where Other is selected.'
          );
          throw new RoleAssignmentSaveError('employee type validation failed');
        }
      };
    }, [
      employeeTypesDirty,
      invalidEmployeeTypeClinicIds,
      member?.roger_user_id,
      showError,
    ]);

    useEffect(() => {
      saveEmployeeTypesRef.current = async () => {
        if (!employeeTypesDirty || !member?.roger_user_id) return;
        try {
          await updateEmployeeTypes({
            userId: member.roger_user_id,
            assignments: getEmployeeTypeAssignments(),
            throwOnError: true,
          });
        } catch (error) {
          console.error('Failed to save employee types:', error);
          showError('Failed to save employee types. Please try again.');
          throw new RoleAssignmentSaveError('employee types save failed');
        }
      };
    }, [
      employeeTypesDirty,
      getEmployeeTypeAssignments,
      member?.roger_user_id,
      showError,
    ]);

    const editUserInfoRef = useRef<EditUserInfoHandle>(null);
    const roleAssignmentRef = useRef<RoleAssignmentHandle>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [isRolesLoading, setIsRolesLoading] = useState(false);
    // The Roger App and Licenses tabs load from the network, so they mount on
    // first visit and then stay mounted rather than reloading on every switch.
    const [visitedTabs, setVisitedTabs] = useState<ReadonlySet<string>>(
      () => new Set(['profile'])
    );
    const handleTabChange = useCallback((tab: string) => {
      setVisitedTabs(prev => (prev.has(tab) ? prev : new Set(prev).add(tab)));
    }, []);
    const selectedAgencyRoleCount = useMemo(
      () => [...roleAssignment.agencySelection.values()].filter(Boolean).length,
      [roleAssignment.agencySelection]
    );

    const dialogTitle = useMemo(() => {
      if (!member) return '';
      const first = member.first_name?.trim() ?? '';
      const last = member.last_name?.trim() ?? '';
      const email = member.email?.trim() ?? '';
      const hasFullName = Boolean(first && last);
      const subject = hasFullName ? `${first} ${last}` : email || 'this member';
      return `Manage ${subject}`;
    }, [member]);

    const handleRogerDocLinkChanged = useCallback(() => {
      onRogerDocLinkChanged?.();
    }, [onRogerDocLinkChanged]);

    const handleSave = useCallback(async () => {
      setIsSaving(true);
      try {
        // Validate ahead of the user-info save too, not just the roles save:
        // otherwise a name edit commits and only then does the dialog reject,
        // which is the same partial save from the other direction.
        try {
          validateEmployeeTypesRef.current();
        } catch {
          // Message already shown by the validator.
          return;
        }
        if (canEditUserInfo && editUserInfoRef.current) {
          try {
            await editUserInfoRef.current.saveIfDirty();
          } catch {
            // Error already shown by EditUserInfoSection; stop here
            return;
          }
        }
        // Roles first (creates user_clinic rows), then disciplines via onAfterSave
        await roleAssignmentRef.current?.save();
      } finally {
        setIsSaving(false);
      }
    }, [canEditUserInfo]);

    return (
      <Dialog
        open={!!member}
        onOpenChange={open => {
          if (!open) onClose();
        }}
      >
        <DialogContent className='flex h-[85vh] min-h-0 flex-col sm:max-w-4xl'>
          <DialogHeader className='pb-0'>
            <DialogTitle className='flex items-center gap-2'>
              <Users className='h-5 w-5 flex-shrink-0 text-muted-foreground' />
              <span className='min-w-0 break-words'>{dialogTitle}</span>
            </DialogTitle>
            <DialogDescription>{member?.email}</DialogDescription>
          </DialogHeader>

          {/* Tabs rather than one long scroll. Every tab stays mounted while
              hidden: the sections hold unsaved edits, and Save reaches into
              each of them. */}
          <Tabs
            defaultValue='profile'
            onValueChange={handleTabChange}
            className='flex min-h-0 flex-1 flex-col'
          >
            <TabsList className='w-fit'>
              <TabsTrigger value='profile'>Profile</TabsTrigger>
              <TabsTrigger value='roles'>
                Roles
                {selectedAgencyRoleCount > 0 && (
                  <span className='ml-1.5 text-xs text-muted-foreground'>
                    {selectedAgencyRoleCount}
                  </span>
                )}
              </TabsTrigger>
              {showEmr && member?.roger_user_id && (
                <TabsTrigger value='clinics'>Clinics</TabsTrigger>
              )}
              <TabsTrigger value='app'>Roger App</TabsTrigger>
              {member?.roger_user_id && canViewLicenses && (
                <TabsTrigger value='licenses'>Licenses</TabsTrigger>
              )}
            </TabsList>

            <div className='-mr-2 min-h-0 flex-1 overflow-y-auto pr-2 pt-4'>
              <TabsContent
                value='profile'
                forceMount
                className='mt-0 flex flex-col gap-4 data-[state=inactive]:hidden'
              >
                {canEditUserInfo && member?.roger_user_id && (
                  <EditUserInfoSection
                    ref={editUserInfoRef}
                    userId={member.roger_user_id}
                    firstName={member.first_name}
                    lastName={member.last_name}
                    phoneNumber={member.phone_number}
                    isRogerEmployee={member.is_roger_employee ?? false}
                    canEditRogerEmployee={isSuperUser}
                  />
                )}
                {showEmr && member?.roger_user_id && (
                  <UserTypeSection
                    ref={saveUserTypeRef}
                    userId={member.roger_user_id}
                    userTypes={member.user_types}
                    showTopSeparator={canEditUserInfo}
                  />
                )}
              </TabsContent>

              <TabsContent
                value='roles'
                forceMount
                className='mt-0 data-[state=inactive]:hidden'
              >
                <RoleAssignmentContent
                  ref={roleAssignmentRef}
                  assignment={roleAssignment}
                  clinics={clinics}
                  onClose={onClose}
                  hideFooter
                  onLoadingChange={setIsRolesLoading}
                  view='agency'
                />
              </TabsContent>

              {showEmr && member?.roger_user_id && (
                <TabsContent
                  value='clinics'
                  forceMount
                  className='mt-0 flex flex-col gap-4 data-[state=inactive]:hidden'
                >
                  <ClinicAccessSection
                    clinics={clinics}
                    accessClinicIds={accessClinicIds}
                    roleSelectedClinicIds={roleSelectedClinicIds}
                    onToggle={toggleAccessClinic}
                    disabled={isSaving}
                  />
                  {showClinicRoles && (
                    <div className='space-y-2'>
                      <Separator />
                      <p className='text-base font-semibold'>
                        Roles at each clinic
                      </p>
                      <RoleAssignmentContent
                        assignment={roleAssignment}
                        clinics={clinics}
                        onClose={onClose}
                        hideFooter
                        clinicDisciplines={clinicDisciplines}
                        view='clinic'
                      />
                    </div>
                  )}
                  <EmployeeTypeSection
                    rows={employeeTypeRows}
                    invalidClinicIds={invalidEmployeeTypeClinicIds}
                    onChangeType={setEmployeeType}
                    onChangeOther={setEmployeeTypeOther}
                  />
                </TabsContent>
              )}

              <TabsContent
                value='app'
                forceMount
                className='mt-0 data-[state=inactive]:hidden'
              >
                {member && visitedTabs.has('app') && (
                  <RogerDocAccountSection
                    member={member}
                    onChanged={handleRogerDocLinkChanged}
                    collapsible={false}
                  />
                )}
              </TabsContent>

              {member?.roger_user_id && canViewLicenses && (
                <TabsContent
                  value='licenses'
                  forceMount
                  className='mt-0 data-[state=inactive]:hidden'
                >
                  {visitedTabs.has('licenses') && (
                    <UserLicensesSection
                      userId={member.roger_user_id}
                      canEdit={canEditLicenses}
                      collapsible={false}
                    />
                  )}
                </TabsContent>
              )}
            </div>
          </Tabs>

          <Separator />

          <DialogFooter>
            <Button
              type='button'
              variant='outline'
              onClick={onClose}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button
              type='button'
              onClick={handleSave}
              disabled={isSaving || isRolesLoading}
            >
              {isSaving && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  });
